import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Suspense, useState } from "react";
import { Text } from "src/components/base/text";
import { Subheading } from "src/components/base/heading";
import { Button } from "src/components/base/button";
import { NoSymbolIcon, PencilSquareIcon, PlusIcon, ServerStackIcon, TrashIcon } from "@heroicons/react/20/solid";
import { ApiInternal } from "src/api/api_internal";
import { FullSplitDnsDomain, FullSplitDnsResolver } from "src/api/generated/frontend_internal_v1";
import AddDomainDialog from "src/components/dialogs/add-domain";
import UpdateDomainDialog from "src/components/dialogs/update-domain";
import AddResolverDialog from "src/components/dialogs/add-resolver";
import UpdateResolverDialog from "src/components/dialogs/update-resolver";
import DeleteResolverDialog from "src/components/dialogs/delete-resolver";
import DeleteDomainDialog from "src/components/dialogs/delete-domain";
import { EmptyState } from "src/components/base/empty-state";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Badge } from "src/components/base/badge";

/**
 * Overview for split DNS.
 *
 * @returns the rendered route component
 */
export default function SplitDnsOverview() {
    const [t] = useTranslation("admin-dns");

    const [openAddDomain, setOpenAddDomain] = useState(false);
    const [openUpdateDomain, setOpenUpdateDomain] = useState<FullSplitDnsDomain | undefined>();
    const [openAddResolver, setOpenAddResolver] = useState<FullSplitDnsDomain | undefined>();
    const [openUpdateResolver, setOpenUpdateResolver] = useState<FullSplitDnsResolver | undefined>();
    const [openDeleteDomain, setOpenDeleteDomain] = useState<FullSplitDnsDomain | undefined>();
    const [openDeleteResolver, setOpenDeleteResolver] = useState<FullSplitDnsResolver | undefined>();

    const router = useRouter();
    const { domains } = Route.useLoaderData();

    const sortedDomains = domains.sort((a, b) => a.domain.localeCompare(b.domain, undefined, { sensitivity: "base" }));

    return (
        <div className={"flex flex-col gap-5"}>
            <div className={"flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"}>
                <div className={"flex flex-col gap-1"}>
                    <Subheading>{t("heading.split-dns")}</Subheading>
                    <Text>{t("description.split-dns")}</Text>
                </div>
                <Button color={"blue"} onClick={() => setOpenAddDomain(true)}>
                    <PlusIcon />
                    {t("button.add-domain")}
                </Button>
            </div>

            {sortedDomains.length === 0 ? (
                <EmptyState
                    icon={<ServerStackIcon />}
                    title={t("empty.split-title")}
                    description={t("empty.split-description")}
                    action={
                        <Button color={"blue"} onClick={() => setOpenAddDomain(true)}>
                            <PlusIcon />
                            {t("button.add-domain")}
                        </Button>
                    }
                />
            ) : (
                sortedDomains.map((domain) => (
                    <DomainSection
                        key={domain.uuid}
                        domain={domain}
                        t={t}
                        onAddResolver={() => setOpenAddResolver(domain)}
                        onEditDomain={() => setOpenUpdateDomain(domain)}
                        onDeleteDomain={() => setOpenDeleteDomain(domain)}
                        onEditResolver={(r) => setOpenUpdateResolver(r)}
                        onDeleteResolver={(r) => setOpenDeleteResolver(r)}
                    />
                ))
            )}

            <Suspense>
                <AddDomainDialog
                    isOpen={openAddDomain}
                    onClose={() => setOpenAddDomain(false)}
                    onSubmit={async () => {
                        setOpenAddDomain(false);
                        await router.invalidate({ sync: true });
                    }}
                />
                <AddResolverDialog
                    domainUuid={openAddResolver?.uuid}
                    isOpen={!!openAddResolver}
                    onClose={() => setOpenAddResolver(undefined)}
                    onSubmit={async () => {
                        setOpenAddResolver(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
                <UpdateDomainDialog
                    domain={openUpdateDomain}
                    isOpen={!!openUpdateDomain}
                    onClose={() => setOpenUpdateDomain(undefined)}
                    onSubmit={async () => {
                        setOpenUpdateDomain(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
                <UpdateResolverDialog
                    resolver={openUpdateResolver}
                    isOpen={!!openUpdateResolver}
                    onClose={() => setOpenUpdateResolver(undefined)}
                    onSubmit={async () => {
                        setOpenUpdateResolver(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
                <DeleteResolverDialog
                    resolver={openDeleteResolver}
                    isOpen={!!openDeleteResolver}
                    onClose={() => setOpenDeleteResolver(undefined)}
                    onDeletion={async () => {
                        setOpenDeleteResolver(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
                <DeleteDomainDialog
                    domain={openDeleteDomain}
                    isOpen={!!openDeleteDomain}
                    onClose={() => setOpenDeleteDomain(undefined)}
                    onDeletion={async () => {
                        setOpenDeleteDomain(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
            </Suspense>
        </div>
    );
}

/**
 * Properties for {@link DomainSection}.
 */
type DomainSectionProps = {
    /** The split-DNS domain to render (with its resolvers). */
    domain: FullSplitDnsDomain;
    /** i18n function from the parent (already namespaced to `admin-dns`). */
    t: (key: string) => string;
    /** Triggered when the user clicks the "Add resolver" button in the resolver table header. */
    onAddResolver: () => void;
    /** Triggered when the user clicks the "Edit domain" button in the header. */
    onEditDomain: () => void;
    /** Triggered when the user clicks the "Delete domain" button in the header. */
    onDeleteDomain: () => void;
    /** Triggered when the user clicks the edit icon on a resolver row. */
    onEditResolver: (resolver: FullSplitDnsResolver) => void;
    /** Triggered when the user clicks the delete icon on a resolver row. */
    onDeleteResolver: (resolver: FullSplitDnsResolver) => void;
};

/**
 * Renders one split-DNS domain section: header (name + globe toggle for implicit records +
 * edit/delete buttons) and a resolver table with an "add resolver" button in its header row.
 *
 * @param props see {@link DomainSectionProps}
 * @returns the rendered section
 */
function DomainSection(props: DomainSectionProps) {
    const { domain, t, onAddResolver, onEditDomain, onDeleteDomain, onEditResolver, onDeleteResolver } = props;
    return (
        <div
            className={
                "divide-y divide-zinc-950/5 overflow-hidden rounded-(--radius-card) bg-(--surface-card) shadow-(--shadow-card-sm) ring-1 ring-zinc-950/5 dark:divide-white/5 dark:ring-white/10"
            }
        >
            <section className={`px-4 py-3 ${domain.disabled ? "opacity-60" : ""}`}>
                <div className={"flex items-center justify-between gap-3"}>
                    <div className={"flex min-w-0 items-center gap-2"}>
                        <span className={"truncate text-sm/5 font-semibold text-zinc-950 dark:text-white"}>
                            {domain.domain}
                        </span>
                        {domain.disabled && (
                            <span
                                className={
                                    "inline-flex items-center gap-1 text-[11px] font-medium text-zinc-500 dark:text-zinc-400"
                                }
                            >
                                <NoSymbolIcon className={"size-3"} />
                                {t("label.disabled")}
                            </span>
                        )}
                    </div>
                    <div className={"flex shrink-0 items-center gap-1"}>
                        <Button plain={true} onClick={onEditDomain}>
                            <PencilSquareIcon />
                        </Button>
                        <Button plain={true} onClick={onDeleteDomain}>
                            <TrashIcon />
                        </Button>
                    </div>
                </div>

                <Table dense={true} striped={true} className={"mt-2 [&_table]:table-fixed"}>
                    <TableHead>
                        <TableRow>
                            <TableHeader className={"w-48"}>{t("table.resolver")}</TableHeader>
                            <TableHeader>{t("table.description")}</TableHeader>
                            <TableHeader className={"w-32"}>{t("table.status")}</TableHeader>
                            <TableHeader className={"w-44"}>
                                <div className={"flex justify-end"}>
                                    <Button
                                        plain={true}
                                        size={"sm"}
                                        className={
                                            "-my-1 font-medium! whitespace-nowrap text-zinc-500! dark:text-zinc-400!"
                                        }
                                        onClick={onAddResolver}
                                    >
                                        <PlusIcon />
                                        {t("button.add-resolver")}
                                    </Button>
                                </div>
                            </TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {domain.resolver.map((resolver) => (
                            <TableRow key={resolver.uuid}>
                                <TableCell
                                    className={"truncate font-mono text-xs/5 text-zinc-700 dark:text-zinc-300"}
                                    title={resolver.ip}
                                >
                                    {resolver.ip}
                                </TableCell>
                                <TableCell className={"truncate"} title={resolver.description}>
                                    {resolver.description}
                                </TableCell>
                                <TableCell>
                                    {resolver.disabled && <Badge color={"cyan"}>{t("label.disabled")}</Badge>}
                                </TableCell>
                                <TableCell>
                                    <div className={"flex justify-end gap-0.5"}>
                                        <Button
                                            plain={true}
                                            title={t("button.edit-resolver")}
                                            onClick={() => onEditResolver(resolver)}
                                        >
                                            <PencilSquareIcon />
                                        </Button>
                                        <Button
                                            plain={true}
                                            title={t("button.delete-resolver")}
                                            onClick={() => onDeleteResolver(resolver)}
                                        >
                                            <TrashIcon />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </section>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/dns/_dns/split")({
    component: SplitDnsOverview,
    loader: async () => {
        const [{ list: domains }] = await Promise.all([ApiInternal.dns.getAllSplitDns()]);
        return { domains };
    },
});
